import { Button as AntButton, Modal, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  deleteNotification,
  getNotification,
  updateNotification,
} from '../../../../api/notification.api';
import useAxios from '../../../../hooks/useAxios';
import useNotification from '../../../../hooks/useNotification';
import {
  IGetNotification,
  INotificationRead,
} from '../../../../models/notification.model';
import Button from '../../../Button';
import ErrorResponse from '../../../ErrorResponse';
import Loader from '../../../Loader';
import ConfirmModal from '../../../modals/ConfirmModal';
import NoticeEditor from '../NoticeEditor';
import NoticeViewer from '../NoticeViewer';
import _ from 'lodash';

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  event: any;
  doGetClassNotice: () => void;
  // tutor dashboard에서 호출인지 여부
  isTutor?: boolean;
  // 학급명
  className?: string | undefined;
  noButton?: boolean;
}

const ShowNoticeModal: FC<IProps> = ({
  visible,
  setVisible,
  event,
  doGetClassNotice,
  isTutor = false,
  className,
  noButton = false,
}) => {
  const { t } = useTranslation();
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetNotification>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();
  const {
    data: deleteRes,
    isLoading: deleteIsLoading,
    error: deleteError,
    execute: deleteExecute,
  } = useAxios();

  const [isEdit, setIsEdit] = useState(false);
  const [toSubmit, setToSubmit] = useState(false);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [readCnt, setReadCnt] = useState<number>(0);

  useEffect(() => {
    if (event) {
      execute(getNotification(event.id));
    }
  }, [event]);

  useEffect(() => {
    let _data: any = _.cloneDeep(res?.data?.notificationRead);
    let tempReadCnt = 0;
      
    if (_data?.length) {
      _data.forEach((item: INotificationRead, idx: number) => {
        if(item.readDateTime) {
          tempReadCnt++;
        }
      });
      setReadCnt(tempReadCnt);
    }
  }, [res]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      doGetClassNotice();
      successNotification(t('common.message.edit1'));
      setVisible(false);
    }
  }, [submitRes, submitIsLoading]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      doGetClassNotice();
      successNotification(t('common.message.delete'));
      setConfirmDeleteVisible(false);
      setVisible(false);
    }
  }, [deleteRes, deleteIsLoading]);

  const submit = (data: FormData) => {
    if (!event) return;
    submitExecute(updateNotification(event.id, data));
  };

  const onDelete = () => {
    if (!event) return;
    deleteExecute(deleteNotification(event.id));
  };

  const columns: TableColumnsType<INotificationRead> = [
    {
      title: t('class.class.column29'),
      dataIndex: 'studentMemberName',
      key: 'studentMemberName',
      align: 'center',
      sorter: (a, b) => a.studentMemberName?.localeCompare(b.studentMemberName),
    },
    {
      title: t('class.class.19'),
      dataIndex: 'readDateTime',
      key: 'readDateTime',
      align: 'center',
      render: (value) => (value ? 'Y' : 'N'),
    },
    {
      title: t('class.class.column96'),
      dataIndex: 'readDateTime',
      key: 'readDateTime',
      align: 'center',
      render: (value) =>
        value ? moment(value).format('YYYY.MM.DD HH:mm') : '-',
    },
  ];

  return (
    <>
      {isLoading || submitIsLoading || deleteIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />
      <ErrorResponse error={deleteError} />

      <Modal
        title={t('class.class.title15')}
        visible={visible}
        width={750}
        onCancel={() => setVisible(false)}
        footer={[
          <div className='comm-btn__area justify-content-center'>	
            <AntButton key="cancel" className='btn-basic pink' onClick={() => setVisible(false)}>
              {t('common.button.confirm')}
            </AntButton>
          </div>
        ]}
      >
        {
          isEdit
            ? (
              <NoticeEditor
                event={event}
                submit={submit}
                toSubmit={toSubmit}
                setToSubmit={setToSubmit}
                isTutor={isTutor}
                data={res?.data}
              />
              )
            : (
              <NoticeViewer
                data={res?.data}
                className={className}
              />
              )
        }
        <div className="text-right mt-3">
          {noButton ? null : isEdit ? (
            <Button
              onClick={() => setToSubmit(true)}
              className="btn btn-primary btn-w100p"
              text={t('common.button.save')}
            />
          ) : (
            <div className="flex flex-row justify-content-center align-items-center">
              <div className="flex flex-fill justify-content-center">
                <h1 className="p-0 m-0">{t('class.class.20')}</h1>
              </div>
              <div className="flex flex-row justify-content-end">
                <Button
                  onClick={() => setConfirmDeleteVisible(true)}
                  className="btn btn-secondary btn-w100p mr-1"
                  text={t('common.button.delete')}
                />
                <Button
                  onClick={() => setIsEdit(true)}
                  className="btn btn-primary btn-w100p"
                  text={t('common.button.edit')}
                />
              </div>
            </div>
          )}
        </div>
        <div>
          <div className='comm-bx__space'>
            <div className='comm-sch__result'>
            {'※' + t('class.class.20') + ' : '} <b>{readCnt}</b>명
            </div>
          </div>
          <div className='comm-table__area mt-0'>
            <Table
              columns={columns}
              dataSource={res?.data?.notificationRead}
              pagination={{
                pageSize: 9999999,
              }}
              rowClassName="pointer"
              rowKey="studentMemberCode"
            />
          </div>
        </div>
      </Modal>

      <ConfirmModal
        visible={confirmDeleteVisible}
        setVisible={setConfirmDeleteVisible}
        onConfirm={onDelete}
        titleText={t('system.menu.title6')}
        messageText={t('system.menu.text3')}
        confirmText={t('common.button.delete')}
        cancelText={t('common.button.cancel')}
      />
    </>
  );
};

export default ShowNoticeModal;
